import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Grid } from '@material-ui/core';

import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { UserContext } from '../../user/User.context';
import { Role, User } from '../../user/User.interface';
import { usePatchUser } from '../../user/User.hooks';
import TrainingChartCard from './cards/TrainingChartCard';
import TrainingLogCard from './cards/TrainingLogCard';
import TraineeExportButton from './components/TraineeExportButton';
import UserInfoCard from '../users/components/UserInfoCard';

const StyledGrid = styled(Grid)`
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`;

const TraineeDetailPage: React.FC = () => {
  const { userId: paramUserId = '' } = useParams<{ userId?: string }>();
  const userId: number = parseInt(paramUserId) || 0;
  const [user, setUser] = useState<User>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const { user: loggedUser } = useContext(UserContext);
  const { user: updatedUser, isLoading, error, patch } = usePatchUser(userId);

  const canActivate: boolean = useMemo(() => {
    return !!loggedUser?.roles?.some((role: Role) => {
      return role.name && ['Admin', 'SiteAdmin'].includes(role.name);
    });
  }, [loggedUser]);

  useEffect(() => {
    if (updatedUser) {
      setDidUpdate(true);
      setUser({ ...user, ...updatedUser });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUser]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>
          Trainee Details - {user?.firstName} {user?.lastName}
        </h2>
        <div style={{ flex: 1 }} />
        <PageTemplateHeaderActions>
          <TraineeExportButton user={user} buttonProps={{ disabled: !user }} />
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <StyledGrid container spacing={3}>
          <Grid item xs={12} md={8} className='grid-flex'>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <UserInfoCard
                  userId={+(userId || 0)}
                  onChange={user => setUser(user)}
                />
              </Grid>
              <Grid item xs={12}>
                <TrainingLogCard userId={+(userId || 0)} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid item xs={12}>
              <TrainingChartCard userId={+(userId || 0)} />
            </Grid>
          </Grid>
        </StyledGrid>
      </PageTemplateDashboard>

      <AppSnackbar severity='error' open={!!error}>
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => setDidUpdate(false)}
      >
        Employee details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default TraineeDetailPage;
